import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  getAllLeadsAsync,
  createLeadAsync,
  updateLeadAsync,
  deleteLeadAsync,
  getLeadByIdAsync,
  selectLeads,
  selectLeadLoading,
  selectLeadError,
  selectLeadSuccess,
  selectLeadMessage,
  resetLeadState,
} from "../store/slices/leadSlice";
import { fetchAllUsers } from "../store/slices/authSlice";
import { useSidebarContext } from "../scenes/global/sidebar/SidebarContext";
import { getAdminLeadStats, getLeadSourceStats } from "../api";
import LeadFollowUpModal from "./LeadFollowUpModal";
import { useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  AreaChart,
  Area
} from "recharts";

import {
  CheckCircle,
  Trash2,
  Edit2,
  Plus,
  Search,
  X,
  ChevronDown,
  RefreshCw,
  Filter,
  ExternalLink,
  Users,
  UserPlus,
  TrendingUp,
  AlertCircle,
  Phone,
  Mail,
  Home,
  Tag,
  ArrowUpRight,
  User,
  CheckCheck,
  Calendar,
  Clock,
  ArrowUp,
  CircleX,
  Megaphone,
  Radio,
  Globe,
  GitBranch,
  Layers,
  MessageSquare,
} from "lucide-react";

// ── Source helpers ────────────────────────────────────────────
const SOURCE_CONFIG = {
  "Walk-in": { label: "Walk-in", color: "text-emerald-400", bg: "bg-emerald-500/10 border-emerald-500/30" },
  "Social Media": { label: "Social", color: "text-pink-400", bg: "bg-pink-500/10 border-pink-500/30" },
  "Reference": { label: "Reference", color: "text-violet-400", bg: "bg-violet-500/10 border-violet-500/30" },
  "Phone": { label: "Phone", color: "text-sky-400", bg: "bg-sky-500/10 border-sky-500/30" },
  "Website": { label: "Website", color: "text-amber-400", bg: "bg-amber-500/10 border-amber-500/30" },
  "Other": { label: "Other", color: "text-gray-400", bg: "bg-gray-500/10 border-gray-500/30" },
};
const SOURCE_ICONS = {
  "Walk-in": User,
  "Social Media": Megaphone,
  "Reference": GitBranch,
  "Phone": Phone,
  "Website": Globe,
  "Other": Layers,
};
const getSourceInfo = (src) => SOURCE_CONFIG[src] || SOURCE_CONFIG["Other"];
const getSourceIcon = (src) => {
  const Ic = SOURCE_ICONS[src] || Layers;
  return <Ic size={11} className="mr-1" />;
};

const LeadManagement = () => {
  const dispatch = useDispatch();
  const { activeSociety, theme, gradient } = useSidebarContext();
  const { activeUser } = useSelector((state) => state.auth);
  const location = useLocation();
  const isPendingView = location.pathname.includes("/pending");
  const isUnassignedView = location.pathname.includes("/unassigned");
  const isAdmin = activeUser?.role === "Admin";

  const leads = useSelector(selectLeads) || [];
  const loading = useSelector(selectLeadLoading);
  const error = useSelector(selectLeadError);
  const success = useSelector(selectLeadSuccess);
  const message = useSelector(selectLeadMessage);
  const users = useSelector((state) => state.auth.users) || [];
  const employeeUsers = users.filter((user) => user.role === "Employee");

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
    status: "new",
    priority: "medium",
    notes: "",
    source: "Walk-in",
    assignedTo: "",
    society: activeSociety?._id || "",
    nextFollowUpDate: "",
    interestedPlot: {
      category: "",
      block: "",
      size: "",
      marla: "",
      plot_type: "Residential",
    },
  });

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [currentLeadId, setCurrentLeadId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [isFilterMenuOpen, setIsFilterMenuOpen] = useState(false);
  const [showNotification, setShowNotification] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState("");
  const [expandedLead, setExpandedLead] = useState(null);
  const [selectedLead, setSelectedLead] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const leadsPerPage = 9;

  // New states for follow-up and statistics
  const [isFollowUpModalOpen, setIsFollowUpModalOpen] = useState(false);
  const [selectedLeadForFollowUp, setSelectedLeadForFollowUp] = useState(null);
  const [adminStats, setAdminStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [sourceStats, setSourceStats] = useState([]);

  useEffect(() => {
    dispatch(getAllLeadsAsync());
    dispatch(fetchAllUsers());

    if (isAdmin) {
      fetchAdminStats();
    }
  }, [dispatch, isAdmin]);

  const fetchAdminStats = async () => {
    setStatsLoading(true);
    try {
      const res = await getAdminLeadStats();
      if (res.success) {
        setAdminStats(res.data);
        if (res.data.sourceDistribution) {
          setSourceStats(res.data.sourceDistribution);
        }
      }
    } catch (error) {
      console.error("Error fetching admin stats:", error);
    } finally {
      setStatsLoading(false);
    }
  };

  useEffect(() => {
    if (success && message) {
      setNotificationMessage(message);
      setShowNotification(true);

      const timer = setTimeout(() => {
        setShowNotification(false);
        dispatch(resetLeadState());
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [success, message, dispatch]);

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name.includes(".")) {
      const [parent, child] = name.split(".");
      setFormData({
        ...formData,
        [parent]: {
          ...formData[parent],
          [child]: value,
        },
      });
    } else {
      setFormData({
        ...formData,
        [name]: value,
      });
    }
  };

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();

    const submitData = {
      ...formData,
      society: activeSociety?._id || "",
    };

    if (isEditing && currentLeadId) {
      dispatch(
        updateLeadAsync({ leadId: currentLeadId, updateData: submitData })
      );
    } else {
      dispatch(createLeadAsync(submitData));
    }

    resetForm();
  };

  // Edit lead
  const handleEditLead = (lead) => {
    setFormData({
      name: lead.name,
      email: lead.email || "",
      phone: lead.phone,
      message: lead.message || "",
      status: lead.status,
      priority: lead.priority || "medium",
      notes: lead.notes || "",
      source: lead.source || "Other",
      assignedTo: lead.assignedTo?._id || lead.assignedTo || "",
      society: lead.society?._id || lead.society || activeSociety?._id || "",
      nextFollowUpDate: lead.nextFollowUpDate ? new Date(lead.nextFollowUpDate).toISOString().split('T')[0] : "",
      interestedPlot: {
        category: lead.interestedPlot?.category || "",
        block: lead.interestedPlot?.block || "",
        size: lead.interestedPlot?.size || "",
        marla: lead.interestedPlot?.marla || "",
        plot_type: lead.interestedPlot?.plot_type || "Residential",
      },
    });

    setIsEditing(true);
    setCurrentLeadId(lead._id);
    setIsFormOpen(true);
  };

  // Delete lead
  const handleDeleteLead = (id) => {
    if (window.confirm("Are you sure you want to delete this lead?")) {
      dispatch(deleteLeadAsync(id));
    }
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      name: "",
      email: "",
      phone: "",
      message: "",
      status: "new",
      priority: "medium",
      notes: "",
      source: "Walk-in",
      assignedTo: "",
      society: activeSociety?._id || "",
      nextFollowUpDate: "",
      interestedPlot: {
        category: "",
        block: "",
        size: "",
        marla: "",
        plot_type: "Residential",
      },
    });

    setIsEditing(false);
    setCurrentLeadId(null);
    setIsFormOpen(false);
  };

  // Filter leads based on active society and role
  const societyLeads = leads.filter((lead) => {
    // 1. Society Filter (Robust check for both string and object IDs)
    if (activeSociety?._id) {
      const leadSocietyId = lead.society?._id || lead.society;
      if (leadSocietyId && String(leadSocietyId) !== String(activeSociety._id)) {
        return false;
      }
    }

    // 2. Role-based visibility
    if (activeUser?.role === "Employee") {
      const currentUserId = activeUser?._id || activeUser?.id;
      if (!currentUserId) return false;

      const assignedId = lead.assignedTo?._id || lead.assignedTo;
      const createdById = lead.createdBy?._id || lead.createdBy;

      // Employee sees lead if they are assigned TO it OR they CREATED it
      const isAssigned = assignedId && String(assignedId) === String(currentUserId);
      const isCreator = createdById && String(createdById) === String(currentUserId);

      return isAssigned || isCreator;
    }

    // Admins see everything within the active society
    return true;
  });

  // Filter leads
  const filteredLeads = societyLeads.filter((lead) => {
    const matchesSearch =
      lead.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lead.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lead.phone.includes(searchTerm);

    const matchesStatus =
      statusFilter === "all" || lead.status === statusFilter;

    // Logic for Pending Follow-ups view (Including Upcoming)
    if (isPendingView) {
      // Show all leads that have a scheduled next follow-up date
      return matchesSearch && matchesStatus && lead.nextFollowUpDate;
    }

    // Logic for Unassigned Hub
    if (isUnassignedView) {
      return matchesSearch && matchesStatus && !lead.assignedTo;
    }

    return matchesSearch && matchesStatus;
  });

  // Sort leads for specific views
  const sortedLeads = [...filteredLeads].sort((a, b) => {
    if (isPendingView) {
      if (!a.nextFollowUpDate) return 1;
      if (!b.nextFollowUpDate) return -1;
      return new Date(a.nextFollowUpDate) - new Date(b.nextFollowUpDate);
    }
    return 0; // Maintain original order for other views
  });

  // Prepare Chart Data
  const statusData = adminStats ? adminStats.statusDistribution.map(s => ({
    name: s._id.charAt(0).toUpperCase() + s._id.slice(1),
    value: s.count
  })) : [];

  const COLORS = ["#3b82f6", "#f59e0b", "#10b981", "#ef4444", "#6366f1"];

  const employeePerformanceData = adminStats ? adminStats.leadsByEmployee.map(emp => ({
    name: emp.employeeName.split(' ')[0],
    leads: emp.totalLeads,
    converted: emp.convertedLeads
  })) : [];

  // Get status badge styling
  const getStatusBadgeClass = (status) => {
    switch (status) {
      case "new": return "bg-blue-500/20 text-blue-400 border border-blue-400/30";
      case "Interested": return "bg-emerald-500/20 text-emerald-400 border border-emerald-400/30";
      case "Not Interested": return "bg-rose-500/20 text-rose-400 border border-rose-400/30";
      case "Pending": return "bg-amber-500/20 text-amber-400 border border-amber-400/30";
      case "contacted": return "bg-cyan-500/20 text-cyan-400 border border-cyan-400/30";
      case "converted": return "bg-teal-500/20 text-teal-400 border border-teal-400/30";
      case "lost": return "bg-gray-500/20 text-gray-400 border border-gray-400/30";
      default: return "bg-gray-500/20 text-gray-400 border border-gray-400/30";
    }
  };

  // Get priority badge styling
  const getPriorityBadgeClass = (priority) => {
    switch (priority) {
      case "high": return "bg-rose-500/20 text-rose-400 border border-rose-400/30";
      case "medium": return "bg-amber-500/20 text-amber-400 border border-amber-400/30";
      case "low": return "bg-emerald-500/20 text-emerald-400 border border-emerald-400/30";
      default: return "bg-gray-500/20 text-gray-400 border border-gray-400/30";
    }
  };

  // Get status icon
  const getStatusIcon = (status) => {
    switch (status) {
      case "new": return <User size={14} className="mr-1" />;
      case "Interested": return <CheckCheck size={14} className="mr-1" />;
      case "Not Interested": return <CircleX size={14} className="mr-1" />;
      case "Pending": return <Clock size={14} className="mr-1" />;
      case "contacted": return <Phone size={14} className="mr-1" />;
      case "converted": return <CheckCircle size={14} className="mr-1" />;
      case "lost": return <CircleX size={14} className="mr-1" />;
      default: return null;
    }
  };

  const indexOfLastLead = currentPage * leadsPerPage;
  const indexOfFirstLead = indexOfLastLead - leadsPerPage;
  const currentLeads = sortedLeads.slice(indexOfFirstLead, indexOfLastLead);
  const totalPages = Math.ceil(sortedLeads.length / leadsPerPage);

  // Function to change page
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  return (
    <div
      className={`min-h-screen ${gradient} ${theme.tailwind.text9} overflow-hidden rounded-xl shadow-lg`}
    >
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header with blurred backdrop */}
        <div className="relative mb-10">
          <div
            className={`absolute inset-0 ${theme.tailwind.bg59} rounded-3xl blur-3xl`}
          ></div>
          <div
            className={`relative z-10 flex flex-col md:flex-row justify-between items-center p-6 rounded-2xl border ${theme.tailwind.border14} backdrop-blur-sm ${theme.tailwind.bg60}`}
          >
            <div className="mb-6 md:mb-0">
              <h1
                className={`text-4xl font-bold ${theme.tailwind.text6} text-transparent bg-clip-text mb-2`}
              >
                {isPendingView ? "Follow-up Queue" : isUnassignedView ? "Unassigned Hub" : "Lead Management"}
              </h1>
              {isPendingView && (
                <p className={`text-sm ${theme.tailwind.text12} opacity-70 mb-2`}>
                  Manage your scheduled follow-ups and upcoming interactions.
                </p>
              )}
              {isUnassignedView && (
                <p className={`text-sm ${theme.tailwind.text12} opacity-70 mb-2`}>
                  Leads that haven't been assigned to any representative yet.
                </p>
              )}
              {activeSociety && (
                <h2
                  className={`text-xl font-medium ${theme.tailwind.text11} flex items-center`}
                >
                  <Home size={18} className="mr-2 opacity-70" />
                  {activeSociety.name}
                </h2>
              )}
            </div>

            <div className="flex items-center space-x-4">
              <button
                onClick={() => setIsFormOpen(true)}
                className={`flex items-center px-5 py-2.5 ${theme.tailwind.text5} rounded-xl font-medium 
                text-white shadow-lg ${theme.tailwind.shadow5} ${theme.tailwind.shadowhover11} transform transition-all duration-300 
                hover:scale-105 active:scale-95 group`}
              >
                <Plus
                  size={20}
                  className="mr-2 group-hover:rotate-90 transition-transform duration-300"
                />
                Add New Lead
              </button>

              <button
                onClick={() => dispatch(getAllLeadsAsync())}
                className={`p-3 ${theme.tailwind.bg3} backdrop-blur-sm rounded-xl border border-slate-600/30
                hover:bg-slate-600/50 transition-all duration-300 hover:scale-105 active:scale-95 group`}
                aria-label="Refresh leads"
              >
                <RefreshCw
                  size={20}
                  className={`${theme.tailwind.text9} ${theme.tailwind.groupHover3
                    } ${loading
                      ? "animate-spin"
                      : "group-hover:rotate-180 transition-transform duration-500"
                    }`}
                />
              </button>
            </div>
          </div>
        </div>

        {/* Stats Summary Cards - Hidden in specialized hub views */}
        {!isPendingView && !isUnassignedView && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
            {[
              {
                title: "Total Leads",
                value: societyLeads?.length || 0,
                icon: <Users size={24} />,
                color: "from-teal-400/20 to-cyan-400/10",
                borderColor: "border-teal-400/20",
              },
              {
                title: "New Leads",
                value:
                  societyLeads?.filter((lead) => lead.status === "new").length ||
                  0,
                icon: <UserPlus size={24} />,
                color: "from-blue-400/20 to-cyan-400/10",
                borderColor: "border-blue-400/20",
              },
              {
                title: "Conversion Rate",
                value: societyLeads?.length
                  ? `${Math.round(
                    (societyLeads.filter((lead) => lead.status === "converted")
                      .length /
                      societyLeads.length) *
                    100
                  )}%`
                  : "0%",
                icon: <TrendingUp size={24} />,
                color: "from-emerald-400/20 to-green-400/10",
                borderColor: "border-emerald-400/20",
              },
              {
                title: "High Priority",
                value:
                  societyLeads?.filter((lead) => lead.priority === "high")
                    .length || 0,
                icon: <AlertCircle size={24} />,
                color: "from-rose-400/20 to-pink-400/10",
                borderColor: "border-rose-400/20",
              },
            ].map((stat, index) => (
              <div
                key={index}
                className={`relative group overflow-hidden rounded-2xl transition-all duration-500 hover:shadow-lg ${theme.tailwind.shadowhover1} border ${stat.borderColor}`}
              >
                <div
                  className={`absolute inset-0 bg-gradient-to-br ${stat.color} opacity-50 group-hover:opacity-70 transition-opacity duration-500`}
                ></div>
                <div
                  className={`absolute inset-0 ${theme.tailwind.bg2} backdrop-blur-sm`}
                ></div>
                <div className="relative z-10 flex justify-between items-center p-6">
                  <div>
                    <h3
                      className={`text-sm font-medium ${theme.tailwind.text4} uppercase tracking-wider ${theme.tailwind.groupHover7} transition-colors`}
                    >
                      {stat.title}
                    </h3>
                    <p className="text-3xl font-bold mt-1 group-hover:scale-110 origin-left transition-transform">
                      {stat.value}
                    </p>
                  </div>
                  <div
                    className={`p-3 ${theme.tailwind.bg1} rounded-xl ${theme.tailwind.groupHoverBg} transition-all duration-300 group-hover:scale-110`}
                  >
                    {stat.icon}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Admin Performance Dashboard - Hidden in specialized hub views */}
        {isAdmin && adminStats && !isPendingView && !isUnassignedView && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-10"
          >
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className={`text-3xl font-black ${theme.tailwind.text10} flex items-center gap-4 tracking-tight`}>
                  <TrendingUp className={`w-9 h-9 ${theme.tailwind.text12} p-2 bg-blue-500/10 rounded-xl`} />
                  Lead Analytics Command Center
                </h2>
                <p className={`text-sm ${theme.tailwind.text12} opacity-70 mt-1 ml-13`}>
                  Real-time tracking of team performance and conversion metrics
                </p>
              </div>
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                onClick={fetchAdminStats}
                className={`flex items-center gap-2 px-6 py-3 rounded-2xl ${theme.tailwind.bg2} border border-white/5 hover:border-blue-500/30 shadow-2xl transition-all font-bold text-sm`}
              >
                <RefreshCw
                  size={18}
                  className={statsLoading ? "animate-spin" : ""}
                />
                Sync Real-time Data
              </motion.button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Visual Charts Container */}
              <div className={`lg:col-span-2 p-8 rounded-[2.5rem] ${theme.tailwind.bg2} border ${theme.tailwind.border17} shadow-2xl relative overflow-hidden group`}>
                <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 to-transparent pointer-events-none" />

                <div className="relative z-10 grid grid-cols-1 md:grid-cols-2 gap-8 h-[320px]">
                  {/* Conversion Pie Chart */}
                  <div className="flex flex-col">
                    <h3 className={`text-sm font-bold uppercase tracking-widest ${theme.tailwind.text12} mb-6 opacity-80 border-l-4 border-blue-500 pl-3`}>
                      Pipeline Stage Distribution
                    </h3>
                    <div className="flex-1 w-full min-h-0">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={statusData}
                            innerRadius={70}
                            outerRadius={95}
                            paddingAngle={10}
                            dataKey="value"
                            stroke="none"
                          >
                            {statusData.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} cornerRadius={12} />
                            ))}
                          </Pie>
                          <Tooltip
                            contentStyle={{ background: '#0f172a', border: '1px solid #1e293b', borderRadius: '16px', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.5)' }}
                            itemStyle={{ color: '#fff', fontSize: '12px', fontWeight: 'bold' }}
                            cursor={{ fill: 'transparent' }}
                          />
                          <Legend verticalAlign="bottom" height={36} iconType="circle" wrapperStyle={{ fontSize: '10px', fontWeight: 'bold', textTransform: 'uppercase', opacity: 0.6 }} />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  </div>

                  {/* Performance Bar Chart */}
                  <div className="flex flex-col">
                    <h3 className={`text-sm font-bold uppercase tracking-widest ${theme.tailwind.text12} mb-6 opacity-80 border-l-4 border-emerald-500 pl-3`}>
                      Top Representative Efficiency
                    </h3>
                    <div className="flex-1 w-full min-h-0">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={employeePerformanceData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                          <CartesianGrid strokeDasharray="5 5" vertical={false} stroke="rgba(255,255,255,0.03)" />
                          <XAxis
                            dataKey="name"
                            axisLine={false}
                            tickLine={false}
                            tick={{ fontSize: 10, fill: '#94a3b8', fontWeight: 'bold' }}
                          />
                          <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: '#94a3b8' }} />
                          <Tooltip
                            cursor={{ fill: 'rgba(255,255,255,0.05)', radius: 8 }}
                            contentStyle={{ background: '#0f172a', border: 'none', borderRadius: '12px' }}
                          />
                          <Bar
                            dataKey="leads"
                            name="Generated"
                            fill="url(#blueGradient)"
                            radius={[8, 8, 0, 0]}
                            barSize={14}
                          />
                          <Bar
                            dataKey="converted"
                            name="Closed"
                            fill="url(#emeraldGradient)"
                            radius={[8, 8, 0, 0]}
                            barSize={14}
                          />
                          <defs>
                            <linearGradient id="blueGradient" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="0%" stopColor="#3b82f6" />
                              <stop offset="100%" stopColor="#2563eb" />
                            </linearGradient>
                            <linearGradient id="emeraldGradient" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="0%" stopColor="#10b981" />
                              <stop offset="100%" stopColor="#059669" />
                            </linearGradient>
                          </defs>
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </div>
              </div>

              {/* Source Distribution Panel */}
              {sourceStats.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`lg:col-span-3 p-6 rounded-[2rem] ${theme.tailwind.bg2} border ${theme.tailwind.border17} shadow-xl`}
                >
                  <h3 className={`text-sm font-bold uppercase tracking-widest ${theme.tailwind.text12} mb-5 opacity-80 border-l-4 border-violet-500 pl-3`}>
                    Lead Source Distribution
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                    {sourceStats.map((src) => {
                      const sid = src._id || "Other";
                      const srcInfo = getSourceInfo(sid);
                      const maxCount = Math.max(...sourceStats.map(s => s.count), 1);
                      const pct = Math.round((src.count / maxCount) * 100);
                      return (
                        <div key={sid} className={`p-4 rounded-2xl border ${srcInfo.bg} flex flex-col gap-2`}>
                          <div className={`text-[10px] font-black uppercase tracking-widest ${srcInfo.color} flex items-center`}>
                            {getSourceIcon(sid)}
                            {srcInfo.label}
                          </div>
                          <div className="text-2xl font-black">{src.count}</div>
                          <div className="h-1.5 rounded-full bg-white/5 overflow-hidden">
                            <div className={`h-full rounded-full bg-current ${srcInfo.color} transition-all`} style={{ width: `${pct}%` }} />
                          </div>
                          <div className={`text-[10px] ${srcInfo.color} opacity-60`}>{src.converted || 0} converted</div>
                        </div>
                      );
                    })}
                  </div>
                </motion.div>
              )}

              {/* Performance Scorecard */}
              <div className="flex flex-col gap-6">
                <motion.div
                  whileHover={{ y: -5 }}
                  className={`p-8 rounded-[2.5rem] bg-gradient-to-br from-indigo-600 to-blue-700 shadow-2xl text-white relative overflow-hidden flex-1 group`}
                >
                  <div className="absolute top-0 right-0 p-6 opacity-10 transform group-hover:scale-110 transition-transform">
                    <TrendingUp size={140} />
                  </div>
                  <div className="relative z-10 flex flex-col h-full justify-between">
                    <div>
                      <p className="text-[10px] font-black uppercase tracking-[4px] opacity-70 mb-6">
                        MVP Representative
                      </p>
                      <h4 className="text-4xl font-black mb-2 flex items-center gap-3">
                        {adminStats.leadsByEmployee[0]?.employeeName.split(' ')[0] || "---"}
                        <div className="bg-yellow-400 p-1.5 rounded-full text-indigo-900">
                          <CheckCheck size={16} />
                        </div>
                      </h4>
                      <p className="text-sm opacity-80 font-medium">
                        Leading team conversion metrics today
                      </p>
                    </div>
                    <div className="mt-8 flex items-end justify-between">
                      <div>
                        <div className="flex items-baseline gap-1">
                          <span className="text-5xl font-black tracking-tighter">{Math.round(adminStats.leadsByEmployee[0]?.conversionRate || 0)}</span>
                          <span className="text-xl font-bold opacity-70">%</span>
                        </div>
                        <p className="text-[10px] font-black uppercase opacity-60 tracking-[2px]">Conversion Win Rate</p>
                      </div>
                      <div className="bg-white/10 p-4 rounded-3xl backdrop-blur-xl border border-white/10">
                        <ArrowUp size={28} />
                      </div>
                    </div>
                  </div>
                </motion.div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Employee Personal Progress Section */}
        {!isAdmin && (
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className={`mb-10 p-8 rounded-[2.5rem] ${theme.tailwind.bg2} border ${theme.tailwind.border17} shadow-2xl relative overflow-hidden`}
          >
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className={`text-2xl font-black ${theme.tailwind.text10} tracking-tight`}>
                  Your Performance Compass
                </h2>
                <p className={`text-sm ${theme.tailwind.text12} opacity-70`}>
                  Track your personal milestones and lead efficiency
                </p>
              </div>
              <div className="flex gap-4">
                <div className="px-5 py-2 rounded-2xl bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 font-bold text-xs uppercase tracking-widest flex items-center gap-2">
                  <CheckCircle size={14} />
                  {societyLeads.filter(l => l.status === 'converted' && l.assignedTo?._id === activeUser?._id).length} Wins
                </div>
                <div className="px-5 py-2 rounded-2xl bg-amber-500/10 border border-amber-500/20 text-amber-400 font-bold text-xs uppercase tracking-widest flex items-center gap-2">
                  <Clock size={14} />
                  {societyLeads.filter(l => l.status === 'contacted' && l.assignedTo?._id === activeUser?._id).length} Active
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {/* Progress Card */}
              <div className="md:col-span-2 bg-black/5 rounded-[2rem] p-6 border border-white/5">
                <div className="flex justify-between items-center mb-6">
                  <span className="text-sm font-bold opacity-60 uppercase tracking-widest">Global Ranking Performance</span>
                  <TrendingUp size={18} className="text-blue-500" />
                </div>
                <div className="h-40">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={[
                      { name: 'Mon', val: 2 },
                      { name: 'Tue', val: 5 },
                      { name: 'Wed', val: 3 },
                      { name: 'Thu', val: 8 },
                      { name: 'Fri', val: 6 },
                      { name: 'Sat', val: 10 },
                      { name: 'Sun', val: 12 },
                    ]}>
                      <Area type="monotone" dataKey="val" stroke="#3b82f6" fillOpacity={1} fill="url(#colorVal)" />
                      <defs>
                        <linearGradient id="colorVal" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} />
                          <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <Tooltip />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Stats Summary */}
              <div className="flex flex-col gap-4">
                <div className="p-6 rounded-[2rem] bg-gradient-to-br from-blue-500/10 to-indigo-500/5 border border-blue-500/10">
                  <p className="text-[10px] font-black uppercase tracking-widest opacity-60 mb-2">Total Leads Handled</p>
                  <span className="text-3xl font-black">{societyLeads.filter(l => l.assignedTo?._id === activeUser?._id).length}</span>
                </div>
                <div className="p-6 rounded-[2rem] bg-gradient-to-br from-emerald-500/10 to-teal-500/5 border border-emerald-500/10">
                  <p className="text-[10px] font-black uppercase tracking-widest opacity-60 mb-2">Win Rate Strategy</p>
                  <span className="text-3xl font-black">
                    {Math.round((societyLeads.filter(l => l.status === 'converted' && l.assignedTo?._id === activeUser?._id).length /
                      (societyLeads.filter(l => l.assignedTo?._id === activeUser?._id).length || 1)) * 100)}%
                  </span>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Notification */}
        {showNotification && (
          <div
            className={`fixed top-4 right-4 ${theme.tailwind.bgr51} backdrop-blur-md border ${theme.tailwind.border11} 
            ${theme.tailwind.text9} px-4 py-3 rounded-xl shadow-lg transform transition-all duration-500 animate-fade-in flex items-center z-50`}
          >
            <CheckCircle size={20} className={`mr-2 ${theme.tailwind.text}`} />
            <span>{notificationMessage}</span>
            <button
              onClick={() => setShowNotification(false)}
              className="ml-4 hover:text-white transition-colors"
            >
              <X size={18} />
            </button>
          </div>
        )}

        {/* Header for Pending View */}
        {isPendingView && (
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className={`mb-8 p-6 rounded-3xl bg-gradient-to-r from-amber-500/10 to-transparent border-l-8 border-amber-500 backdrop-blur-md`}
          >
            <div className="flex items-center gap-4">
              <div className="p-3 bg-amber-500/20 rounded-2xl text-amber-400">
                <Clock size={28} />
              </div>
              <div>
                <h1 className={`text-2xl font-black ${theme.tailwind.text10} tracking-tight`}>
                  Pending Follow-ups
                </h1>
                <p className={`text-sm ${theme.tailwind.text12} opacity-70`}>
                  Leads requiring immediate attention based on scheduled interaction dates
                </p>
              </div>
            </div>
          </motion.div>
        )}

        {/* Search and Filter with floating effect */}
        <div className="relative mb-8">
          <div
            className={`absolute inset-0 ${theme.tailwind.bg59} rounded-2xl blur-xl`}
          ></div>
          <div
            className={`relative z-10 flex flex-col md:flex-row gap-4 p-4 rounded-xl border border-slate-700/50 backdrop-blur-sm ${theme.tailwind.bg60}`}
          >
            <div className="relative flex-1">
              <Search
                size={18}
                className={`absolute left-3 top-3 ${theme.tailwind.text4}`}
              />
              <input
                type="text"
                placeholder="Search leads by name, email or phone..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={`w-full pl-10 pr-4 py-2.5 ${theme.tailwind.bg3} backdrop-blur-sm 
                border border-slate-600/30 rounded-xl focus:outline-none focus:ring-2 ${theme.tailwind.focusRing1}
                ${theme.tailwind.text9} ${theme.tailwind.placeHolder} transition-all duration-300`}
              />
            </div>

            <div className="relative">
              <button
                onClick={() => setIsFilterMenuOpen(!isFilterMenuOpen)}
                className={`flex items-center px-4 py-2.5 ${theme.tailwind.bg3} backdrop-blur-sm 
                border border-slate-600/30 rounded-xl ${theme.tailwind.bgHover10} transition-all duration-300 w-full md:w-auto justify-between`}
              >
                <div className="flex items-center">
                  <Filter size={18} className="mr-2" />
                  {statusFilter === "all"
                    ? "All Status"
                    : statusFilter.charAt(0).toUpperCase() +
                    statusFilter.slice(1)}
                </div>
                <ChevronDown size={18} className="ml-2" />
              </button>

              {isFilterMenuOpen && (
                <div
                  className={`absolute right-0 mt-2 w-48 ${theme.tailwind.bg39} backdrop-blur-md 
                  border border-slate-700/70 rounded-xl shadow-xl z-10 animate-fade-in overflow-hidden`}
                >
                  <ul>
                    {["all", "new", "Interested", "Not Interested", "Pending", "contacted", "converted", "lost"].map(
                      (status) => (
                        <li key={status}>
                          <button
                            onClick={() => {
                              setStatusFilter(status);
                              setIsFilterMenuOpen(false);
                            }}
                            className={`block px-4 py-2.5 w-full text-left ${theme.tailwind.bgHover
                              } transition-colors
                            ${statusFilter === status
                                ? `${theme.tailwind.text9} font-medium ${theme.tailwind.bg25}`
                                : `${theme.tailwind.text4}`
                              }`}
                          >
                            {status === "all"
                              ? "All Status"
                              : status.charAt(0).toUpperCase() +
                              status.slice(1)}
                          </button>
                        </li>
                      )
                    )}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div
            className="mb-6 bg-gradient-to-r from-rose-500/20 to-red-500/20 backdrop-blur-sm border border-rose-500/30 
            text-rose-300 px-4 py-3 rounded-xl animate-pulse"
          >
            {error}
          </div>
        )}

        {/* Leads Cards View - Modern Alternative to Table */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 mb-8">
          <AnimatePresence mode="popLayout">
            {currentLeads?.length > 0 ? (
              currentLeads.map((lead, idx) => (
                <motion.div
                  key={lead._id}
                  initial={{ opacity: 0, scale: 0.9, y: 20 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.9, y: -20 }}
                  transition={{ duration: 0.3, delay: idx * 0.05 }}
                  className={`relative overflow-hidden rounded-[2rem] border border-slate-700/50 transition-all duration-300
                ${expandedLead === lead._id
                      ? `shadow-2xl ${theme.tailwind.shadowhover10} scale-[1.02]`
                      : `hover:shadow-2xl ${theme.tailwind.shadowhover1} hover:-translate-y-1`
                    }`}
                >
                  {/* Background gradient based on status */}
                  <div
                    className={`absolute inset-0 opacity-40 ${lead.status === "new"
                      ? "bg-gradient-to-br from-blue-500/20 to-cyan-500/10"
                      : lead.status === "contacted"
                        ? "bg-gradient-to-br from-amber-500/20 to-yellow-500/10"
                        : lead.status === "converted"
                          ? "bg-gradient-to-br from-emerald-500/20 to-green-500/10"
                          : "bg-gradient-to-br from-rose-500/20 to-red-500/10"
                      }`}
                  ></div>

                  <div className={`absolute inset-0 ${theme.tailwind.bg2} opacity-90 backdrop-blur-xl group-hover:opacity-80 transition-opacity`} />

                  <div className="relative z-10 p-6">
                    {/* Card Header */}
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <h3
                          className={`font-semibold text-lg ${theme.tailwind.text9}`}
                        >
                          {lead.name}
                        </h3>
                        <div className="flex flex-wrap gap-2 mt-2">
                          <span
                            className={`inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-medium
                          ${getStatusBadgeClass(lead.status)}`}
                          >
                            {getStatusIcon(lead.status)}
                            {lead.status.charAt(0).toUpperCase() +
                              lead.status.slice(1)}
                          </span>

                          {lead.priority && (
                            <span
                              className={`inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-medium
                          ${getPriorityBadgeClass(lead.priority)}`}
                            >
                              <ArrowUp
                                size={16}
                                className={`mr-1.5 ${lead.priority === "low"
                                  ? "rotate-180"
                                  : lead.priority === "medium"
                                    ? "rotate-90"
                                    : ""
                                  }`}
                              />
                              {lead.priority.charAt(0).toUpperCase() +
                                lead.priority.slice(1)}
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="flex space-x-1">
                        <button
                          onClick={() => handleEditLead(lead)}
                          className={`p-2 ${theme.tailwind.bg25} rounded-lg ${theme.tailwind.bgHover13}
                        transition-all duration-200 group"
                        aria-label="Edit lead`}
                        >
                          <Edit2
                            size={16}
                            className="group-hover:text-white transition-colors"
                          />
                        </button>
                        <button
                          onClick={() => {
                            setSelectedLeadForFollowUp(lead);
                            setIsFollowUpModalOpen(true);
                          }}
                          className="p-2 bg-emerald-500/10 rounded-lg hover:bg-emerald-500/20
                        transition-all duration-200 group border border-emerald-500/20"
                          title="Log Interaction"
                        >
                          <Phone
                            size={16}
                            className="text-emerald-300 group-hover:text-white transition-colors"
                          />
                        </button>
                        <button
                          onClick={() => handleDeleteLead(lead._id)}
                          className="p-2 bg-rose-500/10 rounded-lg hover:bg-rose-500/20
                        transition-all duration-200 group"
                          aria-label="Delete lead"
                        >
                          <Trash2
                            size={16}
                            className="text-rose-300 group-hover:text-white transition-colors"
                          />
                        </button>
                        <button
                          onClick={() => {
                            dispatch(getLeadByIdAsync(lead._id)).then(() => {
                              setSelectedLead(lead);
                              setShowDetailsModal(true);
                            });
                          }}
                          className="p-2 bg-blue-500/10 rounded-lg hover:bg-blue-500/20
                        transition-all duration-200 group"
                          aria-label="Details lead"
                        >
                          <ExternalLink
                            size={16}
                            className="text-blue-300 group-hover:text-white transition-colors"
                          />
                        </button>
                      </div>
                    </div>
                    {/* Contact Info + Source + Follow-up count */}
                    <div className="space-y-2 mb-4">
                      <div className="flex items-center text-sm">
                        <Mail size={14} className={`mr-2 ${theme.tailwind.text4}`} />
                        <span className={`${theme.tailwind.text22} truncate`}>{lead.email || "—"}</span>
                      </div>
                      <div className="flex items-center text-sm">
                        <Phone size={14} className={`mr-2 ${theme.tailwind.text4}`} />
                        <span className={`${theme.tailwind.text22}`}>{lead.phone}</span>
                      </div>
                      {/* Source + Follow-up count row */}
                      <div className="flex items-center gap-2 flex-wrap mt-1">
                        {lead.source && (
                          <span className={`inline-flex items-center text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border
                            ${getSourceInfo(lead.source).bg} ${getSourceInfo(lead.source).color}`}>
                            {getSourceIcon(lead.source)}
                            {getSourceInfo(lead.source).label}
                          </span>
                        )}
                        <span className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full
                          bg-slate-500/10 border border-slate-500/20 text-slate-400`}>
                          <Clock size={9} />
                          {lead.followUpCount || 0} Follow-up{(lead.followUpCount || 0) !== 1 ? "s" : ""}
                        </span>
                        {lead.nextFollowUpDate && (
                          <span className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full
                            bg-amber-500/10 border border-amber-500/20 text-amber-400`}>
                            <Calendar size={9} />
                            Next: {new Date(lead.nextFollowUpDate).toLocaleDateString()}
                          </span>
                        )}
                      </div>
                    </div>
                    {/* Property & Assignment Info */}
                    <div className="grid grid-cols-2 gap-3 mb-4 text-xs">
                      <div className={`px-3 py-2 rounded-lg ${theme.tailwind.bg17} border border-slate-600/20`}>
                        <div className={`${theme.tailwind.text4} mb-1 flex items-center`}>
                          <Home size={12} className="mr-1.5" /> Property
                        </div>
                        <div className="font-medium">
                          {lead.interestedPlot?.category || "N/A"}
                          {lead.interestedPlot?.block && lead.interestedPlot?.size && (
                            <div className={`${theme.tailwind.text11} mt-1`}>
                              Block {lead.interestedPlot.block},{" "}
                              {lead.interestedPlot.size}
                              {lead.interestedPlot.marla && ` (${lead.interestedPlot.marla} marla)`}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className={`px-3 py-2 rounded-lg ${theme.tailwind.bg17} border border-slate-600/20`}>
                        <div className={`${theme.tailwind.text4} mb-1 flex items-center`}>
                          <User size={12} className="mr-1.5" /> Assigned To
                        </div>
                        <div className="font-medium">
                          {lead.assignedTo && typeof lead.assignedTo === "object"
                            ? lead.assignedTo.username
                            : employeeUsers.find((u) => u._id === lead.assignedTo)?.username || "Unassigned"}
                        </div>
                      </div>
                    </div>
                    {/* Created By */}
                    {lead.createdBy && (
                      <div className={`flex items-center gap-1.5 text-xs ${theme.tailwind.text4} mb-3`}>
                        <UserPlus size={11} className="opacity-60" />
                        <span className="opacity-60">Added by</span>
                        <span className="font-medium opacity-80">
                          {typeof lead.createdBy === "object" ? lead.createdBy.username : "—"}
                        </span>
                      </div>
                    )}
                    {lead.notes && (
                      <div>
                        <h4 className={`text-xs ${theme.tailwind.text4} uppercase tracking-wider mb-2 flex items-center`}>
                          <Tag size={12} className="mr-1.5" /> Notes
                        </h4>
                        <p className={`text-sm ${theme.tailwind.text22} ${theme.tailwind.bg17} p-3 rounded-lg border border-slate-600/10 line-clamp-2`}>
                          {lead.notes}
                        </p>
                      </div>
                    )}
                  </div>
                </motion.div>
              ))
            ) : (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className={`col-span-3 p-16 text-center rounded-2xl ${theme.tailwind.bg60} border border-slate-700/50 backdrop-blur-sm`}
              >
                {loading ? (
                  <div className="flex flex-col justify-center items-center">
                    <RefreshCw
                      size={40}
                      className={`animate-spin mb-4 ${theme.tailwind.text12}`}
                    />
                    <span className="text-xl font-medium">Loading leads...</span>
                  </div>
                ) : (
                  <div>
                    <UserPlus
                      size={48}
                      className={`mx-auto mb-4 ${theme.tailwind.text1}`}
                    />
                    <p className="text-xl font-medium mb-2">
                      {isPendingView
                        ? "No follow-ups scheduled"
                        : isUnassignedView
                          ? "Great job! All leads are assigned"
                          : "No leads found"}
                    </p>
                    <p className={`${theme.tailwind.text4}`}>
                      {isPendingView
                        ? "New follow-ups will appear here automatically when scheduled."
                        : isUnassignedView
                          ? "Check back later for new incoming leads that need assignment."
                          : "Create a new lead to get started with your lead management"}
                    </p>
                    {!isPendingView && !isUnassignedView && (
                      <button
                        onClick={() => setIsFormOpen(true)}
                        className={`mt-6 px-6 py-2.5 ${theme.tailwind.text5} rounded-xl font-medium 
                        text-white shadow-lg ${theme.tailwind.shadow5} ${theme.tailwind.shadowhover11} transform transition-all duration-300 
                        hover:scale-105 active:scale-95 inline-flex items-center`}
                      >
                        <Plus size={20} className="mr-2" />
                        Add New Lead
                      </button>
                    )}
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {filteredLeads.length > leadsPerPage && (
          <div className="flex justify-center mt-10 mb-6">
            <div
              className={`inline-flex items-center rounded-xl ${theme.tailwind.bg2} backdrop-blur-sm border border-slate-700/50 p-1.5`}
            >
              <button
                onClick={() => currentPage > 1 && paginate(currentPage - 1)}
                disabled={currentPage === 1}
                className={`flex items-center justify-center h-10 w-10 rounded-lg mr-1 transition-all duration-200
          ${currentPage === 1
                    ? `opacity-50 cursor-not-allowed ${theme.tailwind.text2}`
                    : `${theme.tailwind.bgHover13} ${theme.tailwind.text9}`
                  }`}
                aria-label="Previous page"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="15 18 9 12 15 6"></polyline>
                </svg>
              </button>

              {Array.from({ length: totalPages }).map((_, index) => {
                if (
                  index === 0 ||
                  index === totalPages - 1 ||
                  (index >= currentPage - 2 && index <= currentPage + 0)
                ) {
                  return (
                    <button
                      key={index}
                      onClick={() => paginate(index + 1)}
                      className={`flex items-center justify-center h-10 w-10 rounded-lg mx-0.5 font-medium transition-all duration-200
                ${currentPage === index + 1
                          ? `${theme.tailwind.text5} text-white shadow-lg ${theme.tailwind.shadow5}`
                          : `${theme.tailwind.bgHover13} ${theme.tailwind.text9}`
                        }`}
                    >
                      {index + 1}
                    </button>
                  );
                } else if (
                  (index === 1 && currentPage > 3) ||
                  (index === totalPages - 2 && currentPage < totalPages - 2)
                ) {
                  return (
                    <span
                      key={index}
                      className={`flex items-center justify-center h-10 w-10 ${theme.tailwind.text4}`}
                    >
                      ...
                    </span>
                  );
                }
                return null;
              })}

              <button
                onClick={() =>
                  currentPage < totalPages && paginate(currentPage + 1)
                }
                disabled={currentPage === totalPages}
                className={`flex items-center justify-center h-10 w-10 rounded-lg ml-1 transition-all duration-200
          ${currentPage === totalPages
                    ? `opacity-50 cursor-not-allowed ${theme.tailwind.text2}`
                    : `${theme.tailwind.bgHover13} ${theme.tailwind.text9}`
                  }`}
                aria-label="Next page"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="9 18 15 12 9 6"></polyline>
                </svg>
              </button>
            </div>
          </div>
        )}

        {/* Lead Form Modal */}
        {isFormOpen && (
          <div
            className={`fixed inset-0 ${theme.tailwind.bg45} backdrop-blur-sm flex justify-center items-center z-50 px-4`}
          >
            <div
              className={`${theme.tailwind.bg8} border border-slate-700/70 rounded-2xl 
              shadow-2xl p-6 w-full max-w-3xl max-h-[90vh] overflow-y-auto animate-scale-in`}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex justify-between items-center mb-6">
                <h2
                  className={`text-2xl font-bold ${theme.tailwind.text6} text-transparent bg-clip-text`}
                >
                  {isEditing ? "Edit Lead" : "Add New Lead"}
                </h2>
                <button
                  onClick={resetForm}
                  className={`p-2 ${theme.tailwind.bgHover15} rounded-lg transition-colors`}
                  aria-label="Close modal"
                >
                  <X size={24} />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Basic Information */}
                <div
                  className={`${theme.tailwind.bg2} rounded-xl p-5 border border-slate-700/50`}
                >
                  <h3 className="text-lg font-semibold mb-4 flex items-center">
                    <User size={18} className={`mr-2 ${theme.tailwind.text}`} />
                    Basic Information
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label
                        className={`block text-sm font-medium ${theme.tailwind.text4} mb-1.5`}
                      >
                        Full Name*
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                        className={`w-full px-4 py-2.5 ${theme.tailwind.bg34} border border-slate-700/50 rounded-lg 
                        focus:outline-none focus:ring-2 ${theme.tailwind.focusRing1} ${theme.tailwind.text9} ${theme.tailwind.placeHolder}"
                        placeholder="Enter client's full name`}
                      />
                    </div>
                    <div>
                      <label
                        className={`block text-sm font-medium ${theme.tailwind.text4} mb-1.5`}
                      >
                        Email Address*
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        className={`w-full px-4 py-2.5 ${theme.tailwind.bg34} border border-slate-700/50 rounded-lg 
                        focus:outline-none focus:ring-2 ${theme.tailwind.focusRing1} ${theme.tailwind.text9} ${theme.tailwind.placeHolder}"
                        placeholder="client@example.com`}
                      />
                    </div>
                    <div>
                      <label
                        className={`block text-sm font-medium ${theme.tailwind.text4} mb-1.5`}
                      >
                        Phone Number*
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        required
                        className={`w-full px-4 py-2.5 ${theme.tailwind.bg34} border border-slate-700/50 rounded-lg 
                        focus:outline-none focus:ring-2 ${theme.tailwind.focusRing1} ${theme.tailwind.text9} ${theme.tailwind.placeHolder}"
                        placeholder="+92 300 1234567`}
                      />
                    </div>
                    {/* Lead Source */}
                    <div>
                      <label className={`block text-sm font-medium ${theme.tailwind.text4} mb-1.5`}>
                        Lead Source *
                      </label>
                      <select
                        name="source"
                        value={formData.source}
                        onChange={handleInputChange}
                        required
                        className={`w-full px-4 py-2.5 ${theme.tailwind.bg34} border border-slate-700/50 rounded-lg 
                        focus:outline-none focus:ring-2 ${theme.tailwind.focusRing1} ${theme.tailwind.text9}`}
                      >
                        <option value="Walk-in">🚶 Walk-in</option>
                        <option value="Social Media">📱 Social Media</option>
                        <option value="Reference">🤝 Reference / Referral</option>
                        <option value="Phone">📞 Phone Inquiry</option>
                        <option value="Website">🌐 Website Form</option>
                        <option value="Other">📌 Other</option>
                      </select>
                    </div>
                    {/* Assigned To */}
                    <div>
                      <label className={`block text-sm font-medium ${theme.tailwind.text4} mb-1.5`}>
                        Assigned To
                      </label>
                      <select
                        name="assignedTo"
                        value={formData.assignedTo}
                        onChange={handleInputChange}
                        className={`w-full px-4 py-2.5 ${theme.tailwind.bg34} border border-slate-700/50 rounded-lg 
                        focus:outline-none focus:ring-2 ${theme.tailwind.focusRing1} ${theme.tailwind.text9}`}
                      >
                        <option value="">Not Assigned</option>
                        {employeeUsers.map((user) => (
                          <option key={user._id} value={user._id}>
                            {user.username}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                {/* Property Interest */}
                <div
                  className={`${theme.tailwind.bg2} rounded-xl p-5 border border-slate-700/50`}
                >
                  <h3 className="text-lg font-semibold mb-4 flex items-center">
                    <Home size={18} className={`mr-2 ${theme.tailwind.text}`} />
                    Property Interest
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label
                        className={`block text-sm font-medium ${theme.tailwind.text4} mb-1.5`}
                      >
                        Property Category
                      </label>
                      <select
                        name="interestedPlot.category"
                        value={formData.interestedPlot.category}
                        onChange={handleInputChange}
                        className={`w-full px-4 py-2.5 ${theme.tailwind.bg34} border border-slate-700/50 rounded-lg 
                        focus:outline-none focus:ring-2 ${theme.tailwind.focusRing1} ${theme.tailwind.text9}`}
                      >
                        <option value="General">General</option>
                        <option value="Residential Plot">
                          Residential Plot
                        </option>
                        <option value="Commercial Plot">Commercial Plot</option>
                        <option value="Villa">Villa</option>
                        <option value="Apartment">Apartment</option>
                        <option value="Farmhouse">Farmhouse</option>
                      </select>
                    </div>
                    <div>
                      <label
                        className={`block text-sm font-medium ${theme.tailwind.text4} mb-1.5`}
                      >
                        Property Type
                      </label>
                      <select
                        name="interestedPlot.plot_type"
                        value={formData.interestedPlot.plot_type}
                        onChange={handleInputChange}
                        className={`w-full px-4 py-2.5 ${theme.tailwind.bg34} border border-slate-700/50 rounded-lg 
                        focus:outline-none focus:ring-2 ${theme.tailwind.focusRing1} ${theme.tailwind.text9}`}
                      >
                        <option value="Residential">Residential</option>
                        <option value="Commercial">Commercial</option>
                        <option value="Mixed Use">Mixed Use</option>
                      </select>
                    </div>
                    <div>
                      <label
                        className={`block text-sm font-medium ${theme.tailwind.text4} mb-1.5`}
                      >
                        Block/Area
                      </label>
                      <input
                        type="text"
                        name="interestedPlot.block"
                        value={formData.interestedPlot.block}
                        onChange={handleInputChange}
                        className={`w-full px-4 py-2.5 ${theme.tailwind.bg34} border border-slate-700/50 rounded-lg 
                        focus:outline-none focus:ring-2 ${theme.tailwind.focusRing1} ${theme.tailwind.text9} ${theme.tailwind.placeHolder}`}
                        placeholder="e.g. Block A, Sector 7"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label
                          className={`block text-sm font-medium ${theme.tailwind.text4} mb-1.5`}
                        >
                          Size
                        </label>
                        <input
                          type="text"
                          name="interestedPlot.size"
                          value={formData.interestedPlot.size}
                          onChange={handleInputChange}
                          className={`w-full px-4 py-2.5 ${theme.tailwind.bg34} border border-slate-700/50 rounded-lg 
                          focus:outline-none focus:ring-2 ${theme.tailwind.focusRing1} ${theme.tailwind.text9} ${theme.tailwind.placeHolder}`}
                          placeholder="e.g. 250 sqyd"
                        />
                      </div>
                      <div>
                        <label
                          className={`block text-sm font-medium ${theme.tailwind.text4} mb-1.5`}
                        >
                          Marla
                        </label>
                        <input
                          type="text"
                          name="interestedPlot.marla"
                          value={formData.interestedPlot.marla}
                          onChange={handleInputChange}
                          className={`w-full px-4 py-2.5 ${theme.tailwind.bg34} border border-slate-700/50 rounded-lg 
                          focus:outline-none focus:ring-2 ${theme.tailwind.focusRing1} ${theme.tailwind.text9} ${theme.tailwind.placeHolder}`}
                          placeholder="e.g. 10"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Lead Status & Priority */}
                <div
                  className={`${theme.tailwind.bg2} rounded-xl p-5 border border-slate-700/50`}
                >
                  <h3 className="text-lg font-semibold mb-4 flex items-center">
                    <ArrowUpRight
                      size={18}
                      className={`mr-2 ${theme.tailwind.text}`}
                    />
                    Lead Status & Priority
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label
                        className={`block text-sm font-medium ${theme.tailwind.text4} mb-1.5`}
                      >
                        Status
                      </label>
                      <select
                        name="status"
                        value={formData.status}
                        onChange={handleInputChange}
                        className={`w-full px-4 py-2.5 ${theme.tailwind.bg34} border border-slate-700/50 rounded-lg 
                        focus:outline-none focus:ring-2 ${theme.tailwind.focusRing1} ${theme.tailwind.text9}`}
                      >
                        <option value="new">New</option>
                        <option value="Interested">Interested</option>
                        <option value="Not Interested">Not Interested</option>
                        <option value="Pending">Pending</option>
                        <option value="contacted">Contacted</option>
                        <option value="converted">Converted</option>
                        <option value="lost">Lost</option>
                      </select>
                    </div>
                    <div>
                      <label
                        className={`block text-sm font-medium ${theme.tailwind.text4} mb-1.5`}
                      >
                        Priority
                      </label>
                      <select
                        name="priority"
                        value={formData.priority}
                        onChange={handleInputChange}
                        className={`w-full px-4 py-2.5 ${theme.tailwind.bg34} border border-slate-700/50 rounded-lg 
                        focus:outline-none focus:ring-2 ${theme.tailwind.focusRing1} ${theme.tailwind.text9}`}
                      >
                        <option value="low">Low</option>
                        <option value="medium">Medium</option>
                        <option value="high">High</option>
                      </select>
                    </div>
                    <div>
                      <label
                        className={`block text-sm font-medium ${theme.tailwind.text4} mb-1.5`}
                      >
                        Next Follow-up Date
                      </label>
                      <input
                        type="date"
                        name="nextFollowUpDate"
                        value={formData.nextFollowUpDate}
                        onChange={handleInputChange}
                        className={`w-full px-4 py-2.5 ${theme.tailwind.bg34} border border-slate-700/50 rounded-lg 
                        focus:outline-none focus:ring-2 ${theme.tailwind.focusRing1} ${theme.tailwind.text9}`}
                      />
                    </div>
                  </div>
                </div>

                {/* Message & Notes */}
                <div
                  className={`${theme.tailwind.bg2} rounded-xl p-5 border border-slate-700/50`}
                >
                  <h3 className="text-lg font-semibold mb-4 flex items-center">
                    <Mail size={18} className={`mr-2 ${theme.tailwind.text}`} />
                    Message & Notes
                  </h3>
                  <div className="space-y-4">
                    <div>
                      <label
                        className={`block text-sm font-medium ${theme.tailwind.text4} mb-1.5`}
                      >
                        Client Message
                      </label>
                      <textarea
                        name="message"
                        value={formData.message}
                        onChange={handleInputChange}
                        rows={3}
                        className={`w-full px-4 py-2.5 ${theme.tailwind.bg34} border border-slate-700/50 rounded-lg 
                        focus:outline-none focus:ring-2 ${theme.tailwind.focusRing1} ${theme.tailwind.text9} ${theme.tailwind.placeHolder} resize-none`}
                        placeholder="Enter message from client..."
                      ></textarea>
                    </div>
                    <div>
                      <label
                        className={`block text-sm font-medium ${theme.tailwind.text4} mb-1.5`}
                      >
                        Internal Notes
                      </label>
                      <textarea
                        name="notes"
                        value={formData.notes}
                        onChange={handleInputChange}
                        rows={3}
                        className={`w-full px-4 py-2.5 ${theme.tailwind.bg34} border border-slate-700/50 rounded-lg 
                        focus:outline-none focus:ring-2 ${theme.tailwind.focusRing1} ${theme.tailwind.text9} ${theme.tailwind.placeHolder} resize-none`}
                        placeholder="Add internal notes about this lead..."
                      ></textarea>
                    </div>
                  </div>
                </div>

                {/* Form Buttons */}
                <div className="flex justify-end space-x-3 pt-4 border-t border-slate-700/30">
                  <button
                    type="button"
                    onClick={resetForm}
                    className={`px-5 py-2.5 ${theme.tailwind.bg3} hover:bg-slate-700 
                    rounded-xl transition-colors duration-300`}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className={`px-6 py-2.5 ${theme.tailwind.text5} rounded-xl font-medium 
                    text-white shadow-lg ${theme.tailwind.shadow5} ${theme.tailwind.shadowhover11} transform transition-all duration-300 
                    hover:scale-105 active:scale-95 flex items-center`}
                  >
                    {loading ? (
                      <RefreshCw size={20} className="animate-spin mr-2" />
                    ) : (
                      <CheckCircle size={20} className="mr-2" />
                    )}
                    {isEditing ? "Update Lead" : "Save Lead"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {showDetailsModal && selectedLead && (
          <div
            className={`fixed inset-0 ${theme.tailwind.bg45} backdrop-blur-sm flex justify-center items-center z-50 px-4`}
          >
            <div
              className={`${theme.tailwind.bg8} border border-slate-700/70 rounded-2xl 
      shadow-2xl p-6 w-full max-w-3xl max-h-[90vh] overflow-y-auto animate-scale-in`}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex justify-between items-center mb-6">
                <h2
                  className={`text-2xl font-bold ${theme.tailwind.text6} text-transparent bg-clip-text`}
                >
                  Lead Details
                </h2>
                <button
                  onClick={() => setShowDetailsModal(false)}
                  className={`p-2 ${theme.tailwind.bgHover15} rounded-lg transition-colors`}
                  aria-label="Close modal"
                >
                  <X size={24} />
                </button>
              </div>

              {/* Lead Details Section */}
              <div className="space-y-6">
                {/* Basic Information */}
                <div
                  className={`${theme.tailwind.bg2} rounded-xl p-5 border border-slate-700/50`}
                >
                  <h3 className="text-lg font-semibold mb-4 flex items-center">
                    <User size={18} className={`mr-2 ${theme.tailwind.text}`} />
                    Basic Information
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <p className={`text-sm ${theme.tailwind.text4}`}>
                        Full Name
                      </p>
                      <p className="text-lg font-medium">{selectedLead.name}</p>
                    </div>
                    <div>
                      <p className={`text-sm ${theme.tailwind.text4}`}>
                        Email Address
                      </p>
                      <p className="text-lg font-medium">
                        {selectedLead.email}
                      </p>
                    </div>
                    <div>
                      <p className={`text-sm ${theme.tailwind.text4}`}>
                        Phone Number
                      </p>
                      <p className="text-lg font-medium">
                        {selectedLead.phone}
                      </p>
                    </div>
                    <div>
                      <p className={`text-sm ${theme.tailwind.text4}`}>
                        Assigned To
                      </p>
                      <p className="text-lg font-medium">
                        {selectedLead.assignedTo &&
                          typeof selectedLead.assignedTo === "object"
                          ? selectedLead.assignedTo.username
                          : employeeUsers.find(
                            (user) => user._id === selectedLead.assignedTo
                          )?.username || "Unassigned"}
                      </p>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2 mt-3">
                    <span
                      className={`inline-flex items-center px-3 py-1.5 rounded-lg text-sm font-medium
              ${getStatusBadgeClass(selectedLead.status)}`}
                    >
                      {getStatusIcon(selectedLead.status)}
                      {selectedLead.status.charAt(0).toUpperCase() +
                        selectedLead.status.slice(1)}
                    </span>

                    {selectedLead.priority && (
                      <span
                        className={`inline-flex items-center px-3 py-1.5 rounded-lg text-sm font-medium
                ${getPriorityBadgeClass(selectedLead.priority)}`}
                      >
                        <ArrowUp
                          size={16}
                          className={`mr-1.5 ${selectedLead.priority === "low"
                            ? "rotate-180"
                            : selectedLead.priority === "medium"
                              ? "rotate-90"
                              : ""
                            }`}
                        />
                        {selectedLead.priority.charAt(0).toUpperCase() +
                          selectedLead.priority.slice(1)}
                      </span>
                    )}
                  </div>
                </div>

                {/* Property Interest */}
                <div
                  className={`${theme.tailwind.bg2} rounded-xl p-5 border border-slate-700/50`}
                >
                  <h3 className="text-lg font-semibold mb-4 flex items-center">
                    <Home size={18} className={`mr-2 ${theme.tailwind.text}`} />
                    Property Interest
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <p className={`text-sm ${theme.tailwind.text4}`}>
                        Property Category
                      </p>
                      <p className="text-base font-medium">
                        {selectedLead.interestedPlot?.category || "N/A"}
                      </p>
                    </div>
                    <div>
                      <p className={`text-sm ${theme.tailwind.text4}`}>
                        Property Type
                      </p>
                      <p className="text-base font-medium">
                        {selectedLead.interestedPlot?.plot_type || "N/A"}
                      </p>
                    </div>
                    <div>
                      <p className={`text-sm ${theme.tailwind.text4}`}>
                        Block/Area
                      </p>
                      <p className="text-base font-medium">
                        {selectedLead.interestedPlot?.block || "N/A"}
                      </p>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <p className={`text-sm ${theme.tailwind.text4}`}>
                          Size
                        </p>
                        <p className="text-base font-medium">
                          {selectedLead.interestedPlot?.size || "N/A"}
                        </p>
                      </div>
                      <div>
                        <p className={`text-sm ${theme.tailwind.text4}`}>
                          Marla
                        </p>
                        <p className="text-base font-medium">
                          {selectedLead.interestedPlot?.marla || "N/A"}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Message & Notes */}
                <div
                  className={`${theme.tailwind.bg2} rounded-xl p-5 border border-slate-700/50`}
                >
                  <h3 className="text-lg font-semibold mb-4 flex items-center">
                    <Mail size={18} className={`mr-2 ${theme.tailwind.text}`} />
                    Message & Notes
                  </h3>
                  <div className="space-y-4">
                    {selectedLead.message && (
                      <div>
                        <h4 className={`text-sm ${theme.tailwind.text4} mb-2`}>
                          Client Message
                        </h4>
                        <p
                          className={`text-base ${theme.tailwind.bg17} p-3 rounded-lg border border-slate-600/10`}
                        >
                          {selectedLead.message}
                        </p>
                      </div>
                    )}
                    {selectedLead.notes && (
                      <div>
                        <h4 className={`text-sm ${theme.tailwind.text4} mb-2`}>
                          Internal Notes
                        </h4>
                        <p
                          className={`text-base ${theme.tailwind.bg17} p-3 rounded-lg border border-slate-600/10`}
                        >
                          {selectedLead.notes}
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Created & Updated Timestamps */}
                <div
                  className={`${theme.tailwind.bg2} rounded-xl p-5 border border-slate-700/50`}
                >
                  <h3 className="text-lg font-semibold mb-4 flex items-center">
                    <Calendar
                      size={18}
                      className={`mr-2 ${theme.tailwind.text}`}
                    />
                    Timestamps
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {selectedLead.createdAt && (
                      <div>
                        <p
                          className={`text-sm ${theme.tailwind.text4} flex items-center`}
                        >
                          <Calendar size={12} className="mr-1.5" /> Created At
                        </p>
                        <p className="text-base font-medium">
                          {new Date(selectedLead.createdAt).toLocaleString()}
                        </p>
                      </div>
                    )}
                    {selectedLead.updatedAt && (
                      <div>
                        <p
                          className={`text-sm ${theme.tailwind.text4} flex items-center`}
                        >
                          <Clock size={12} className="mr-1.5" /> Last Updated
                        </p>
                        <p className="text-base font-medium">
                          {new Date(selectedLead.updatedAt).toLocaleString()}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        <LeadFollowUpModal
          isOpen={isFollowUpModalOpen}
          onClose={() => setIsFollowUpModalOpen(false)}
          lead={selectedLeadForFollowUp}
          onUpdate={(newLeadStatus) => {
            dispatch(getAllLeadsAsync());
            if (isAdmin) fetchAdminStats();
          }}
        />
      </div>
    </div>
  );
};

export default LeadManagement;
